"use client";
/**
 * SANATAN DECODE — v15 "POSTER". 21s @ 30fps, 1280x720.
 *
 * The reference repo's loudest instruction, and the one thing every previous version has
 * been timid about: BOLD FLAT COLOUR PER BEAT, OVERSIZED CUTOUT HEADLINES. Every version so
 * far has kept the same cream ground for 21 seconds and set type at 70px on top of it.
 *
 * v15 inverts the stack. The frame IS a colour — cream, gold, ink, kumkum, bone — swapped at
 * every beat with a hard wipe, and the photographic ground survives only through a moving
 * APERTURE: a single window onto the walked panorama that travels and resizes beat to beat.
 * Type runs 96-132px and is allowed to overhang the frame edge.
 *
 * LAW 4 is doing real work here: on the ink and kumkum beats the accent flips to AMBER,
 * because deep kumkum vanishes on dark. LAW 5 is free — a cutout on a flat field cannot merge
 * with the ground. LAW 8 is respected: no new elements, only a different ground and scale.
 */
import React from "react";
import { AbsoluteFill, Audio, staticFile, interpolate, Easing } from "remotion";
import {
  INK, GOLD, MARIGOLD, KUMKUM, MUTED_C, CREAM, deva, inter,
  f, useStep, useKeys, useSpringAt, W,
  Pano, Grain, Word, Row, Line, Beat, useCamera, useSwing, Prop, PersistentThread, Furniture,
} from "./sanatan/kit";

const BONE = "#F4EDDD";
const AMBER = "#F0913A";          // the accent on dark grounds (LAW 4)

/* ══ THE FIELD ═════════════════════════════════════════════════
   One flat colour per beat, arriving as a hard directional wipe — never a cross-fade
   (LAW 2). The wipe direction alternates so consecutive beats do not feel like a slideshow. */
type Field = { at: number; color: string; dir: "l" | "r" | "u" | "d" };
const FIELDS: Field[] = [
  { at: -1, color: "#F4E7C6", dir: "l" },
  { at: 3.86, color: "#E9B84E", dir: "r" },
  { at: 5.76, color: INK, dir: "u" },
  { at: 10.56, color: "#B5382C", dir: "l" },
  { at: 15.74, color: BONE, dir: "d" },
];

const Fields: React.FC = () => {
  const frame = useStep();
  return (
    <AbsoluteFill style={{ backgroundColor: FIELDS[0].color }}>
      {FIELDS.map((s, i) => {
        const p = interpolate(frame, [f(s.at), f(s.at) + 9], [0, 1], {
          extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.out(Easing.cubic),
        });
        if (p <= 0) return null;
        const q = ((1 - p) * 100).toFixed(2);
        const inset = s.dir === "l" ? `0 ${q}% 0 0` : s.dir === "r" ? `0 0 0 ${q}%`
          : s.dir === "u" ? `${q}% 0 0 0` : `0 0 ${q}% 0`;
        return <AbsoluteFill key={i} style={{ backgroundColor: s.color, clipPath: `inset(${inset})` }} />;
      })}
    </AbsoluteFill>
  );
};

/* ══ THE APERTURE ══════════════════════════════════════════════
   The walked panorama is not gone — it is the one window in the poster, and it MOVES, so the
   film still reads as travelling through a place. Keeping the walk intact is what stops this
   version regressing to the flat vector look that was rejected at v1.                        */
const APERTURE: { at: number; x: number; y: number; w: number; h: number; r: number }[] = [
  { at: 0.0, x: 792, y: 300, w: 640, h: 470, r: 320 },
  { at: 3.9, x: 356, y: 330, w: 470, h: 470, r: 235 },
  { at: 5.8, x: 900, y: 250, w: 620, h: 330, r: 22 },
  { at: 10.6, x: 330, y: 250, w: 560, h: 330, r: 22 },
  { at: 15.8, x: 840, y: 320, w: 700, h: 420, r: 210 },
];
const useAperture = () => {
  const t = APERTURE.map((a) => a.at);
  const e = { extrapolateLeft: "clamp" as const, extrapolateRight: "clamp" as const, easing: Easing.inOut(Easing.cubic) };
  const frame = useStep();
  const at = (k: keyof (typeof APERTURE)[0]) => interpolate(frame, t.map(f), APERTURE.map((a) => a[k] as number), e);
  return { x: at("x"), y: at("y"), w: at("w"), h: at("h"), r: at("r") };
};

const Window: React.FC = () => {
  const { x, y, w, h, r } = useAperture();
  return (
    <div style={{
      position: "absolute", left: x - w / 2, top: y - h / 2, width: w, height: h,
      borderRadius: r, overflow: "hidden",
      boxShadow: "0 26px 40px rgba(48,30,10,0.34), 0 0 0 6px rgba(255,250,236,0.9)",
    }}>
      {/* less blur than usual: inside a hard window the photo can be sharper without ever
          fighting the type, because the type never crosses the frame. */}
      <Pano blur={2.2} sat={0.72} bright={1.06} contrast={0.92} wash={0.14} vignette={false} />
    </div>
  );
};

/* ══ BEATS ═════════════════════════════════════════════════════ */
const BeatA: React.FC = () => (
  <>
    <Prop src="sanatan/manuscript.png" w={560} left={70} top={430} inAt={-0.4} outAt={3.55} from="left" amp={11} travel={140} />
    <div style={{ position: "absolute", left: 62, top: 96, width: 700 }}>
      <div style={{ textAlign: "left" }}>
        <Row gap={12}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={34} color={MUTED_C} weight={600} underline={i === 2} outAt={3.6} halo={false} />)}</Row>
      </div>
      <div style={{ height: 10 }} />
      {/* oversized, ranged left, allowed to run past the column */}
      <div style={{ marginLeft: -6, width: 820 }}>
        <div style={{ textAlign: "left" }}>
          <Row gap={12}>
            <Word idx={8} size={104} weight={900} outAt={3.62} halo={false} />
            <Word idx={9} size={104} weight={900} outAt={3.64} halo={false} />
          </Row>
        </div>
        <div style={{ textAlign: "left" }}>
          <Row gap={12}>
            <Word idx={10} size={112} weight={900} color={KUMKUM} strike outAt={3.66} halo={false} />
            <Word idx={11} size={104} weight={900} outAt={3.68} halo={false} />
            <Word idx={12} size={104} weight={900} outAt={3.7} halo={false} />
          </Row>
        </div>
      </div>
    </div>
  </>
);

/** Gold field. One word at 132px, ranged right, the rishi entering from the left edge. */
const BeatB: React.FC = () => (
  <>
    <Prop src="sanatan/rishi.png" h={392} left={676} top={150} inAt={3.95} outAt={5.5} from="right" amp={13} travel={220} />
    <div style={{ position: "absolute", left: 58, top: 250, width: 560, textAlign: "left" }}>
      <Row gap={0}><Word idx={15} size={132} weight={900} color={"#5B2317"} underline outAt={5.5} halo={false} /></Row>
      <div style={{ height: 6 }} />
      <Line text="एक नाम, एक पूर्वज" at={4.85} outAt={5.5} size={26} color={"#7A3A20"} weight={800} halo={false} />
    </div>
  </>
);

/** INK field — the accent flips to AMBER, and the type goes cream. */
const BeatC: React.FC = () => (
  <>
    <Prop src="sanatan/modern_man.png" h={352} left={92} top={300} inAt={7.0} outAt={10.25} from="left" amp={15} />
    <div style={{ position: "absolute", left: 470, top: 132, width: 760, textAlign: "left" }}>
      <Line text="ऋषि से आप तक" at={6.2} outAt={10.2} size={28} color={"#C9BFA6"} weight={700} halo={false} />
      <div style={{ height: 12 }} />
      <Row gap={14}>
        <Word idx={26} size={76} weight={800} color={CREAM} outAt={10.2} halo={false} />
        <Word idx={27} size={76} weight={800} color={CREAM} outAt={10.22} halo={false} />
      </Row>
      <Row gap={14}>
        <Word idx={28} size={100} weight={900} color={AMBER} underline outAt={10.24} halo={false} />
      </Row>
    </div>
    {/* the generation chain, set small and quiet under the headline */}
    <div style={{ position: "absolute", left: 474, top: 470, display: "flex", gap: 26 }}>
      {([["पुत्र", 6.80], ["पौत्र", 6.98], ["प्रपौत्र", 7.16], ["कई पीढ़ी", 7.36]] as [string, number][]).map(([t, at]) => (
        <Line key={t} text={t} at={at} outAt={10.2} size={22} color={"#A79C82"} weight={700} halo={false} />
      ))}
    </div>
  </>
);

/** KUMKUM field — the gravest beat gets the loudest colour, cream type, amber accent. */
const BeatDE: React.FC = () => {
  const tag = useSpringAt(11.61, 10, 150);
  const tagOut = useSpringAt(15.4, 200, 80);
  return (
    <>
      <Prop src="sanatan/couple.png" w={396} left={800} top={78} inAt={10.65} outAt={15.42} from="top" amp={11} travel={280} />
      <div style={{ position: "absolute", left: 62, top: 168, width: 700, textAlign: "left" }}>
        <div style={{
          display: "inline-block", fontFamily: deva, fontSize: 22, fontWeight: 800, wordSpacing: 8,
          color: "#B5382C", background: "#FFF3D6", padding: "6px 26px", borderRadius: 3,
          opacity: Math.min(1, tag * 1.5) * (1 - tagOut),
          transform: `translateY(${(1 - tag) * 18}px) rotate(${-5 + 6 * (1 - tag)}deg)`,
        }}>{W[34][2]}</div>
        <div style={{ height: 22 }} />
        <Row gap={16}>
          <Word idx={38} size={96} weight={900} color={CREAM} outAt={15.4} halo={false} />
          <Word idx={39} size={96} weight={900} color={CREAM} outAt={15.42} halo={false} />
        </Row>
        <Row gap={0}>
          <Word idx={40} size={112} weight={900} color={"#FFD98A"} outAt={15.44} halo={false} />
        </Row>
      </div>
    </>
  );
};

/** Bone field — back to light, so the accent returns to kumkum. */
const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 330, PY = 232, CY = 392;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);
  const bob = Math.sin(frame / 24) * 5;
  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 6}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={44} fill="#FFFBF0" stroke={INK} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.22} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );
  return (
    <>
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 118} s={pA} ph={0} />
        <Carrier x={CX + 118} s={pB} ph={40} />
        <g opacity={kid} transform={`translate(${CX},${CY + bob}) scale(${0.78 + kid * 0.22}) rotate(${-10 * (1 - kid)})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <div style={{
        position: "absolute", left: 58, top: 478, width: 620, textAlign: "left",
        opacity: Math.min(1, term * 1.4),
        transform: `translateY(${(1 - term) * 34}px) rotate(${3 * (1 - term)}deg)`,
      }}>
        <div style={{ fontFamily: inter, fontSize: 52, fontWeight: 800, letterSpacing: 4, color: KUMKUM, lineHeight: 1.05 }}>CONSANGUINITY</div>
        <div style={{ height: 10 }} />
        <div style={{ fontFamily: deva, fontSize: 26, fontWeight: 700, color: INK, opacity: 0.82 }}>विज्ञान ने इसे एक नाम दिया।</div>
      </div>
    </>
  );
};

export const SanatanDecodeGotraV15: React.FC = () => {
  const cam = useCamera();
  const frame = useStep();
  // the wordmark has to survive an ink and a kumkum field, so it inverts with them
  const dark = frame >= f(5.76) && frame < f(15.74);
  return (
    <AbsoluteFill style={{ backgroundColor: "#F4E7C6", overflow: "hidden" }}>
      <Audio src={staticFile("sanatan/gotra.mp3")} />
      <Fields />
      <Window />
      <AbsoluteFill style={{ transform: cam }}>
        <Beat from={0} to={3.75}><BeatA /></Beat>
        <Beat from={3.9} to={5.6}><BeatB /></Beat>
        <Beat from={5.8} to={10.4}><BeatC /></Beat>
        <Beat from={10.6} to={15.6}><BeatDE /></Beat>
        <Beat from={15.8} to={21.1}><BeatF /></Beat>
        <PersistentThread width={5} />
      </AbsoluteFill>
      <Grain op={0.10} />
      <Furniture color={dark ? CREAM : INK} op={dark ? 0.8 : 0.6} />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
